import { useMemo } from 'react';
import _ from 'underscore';

import { LogUtils } from '@bingads-webui-clientcenter/common-utils';

import { defaultLogger } from './shape';

const { logPageLoadAndNavigationDuration } = LogUtils;

export interface InstrumentationLogging extends LogUtils.PageLoadLogging {
  trace?: (scenarioName: string, message?: string) => unknown;
  error?: (scenarioName: string, message?: string) => unknown;
  startScenario?: (scenarioName: string, message?: unknown) => unknown;
  endScenario?: (scenarioName: string, result?: unknown, message?: unknown) => unknown;
}

export interface LoggerParams {
  defaultScenarioName?: string;
  logging?: InstrumentationLogging | null;
}

export interface LoggerMessageParams {
  scenarioName?: string;
  message?: unknown;
}

export interface EndScenarioParams extends LoggerMessageParams {
  result?: unknown;
}

export type PageLoadParams = Partial<Pick<LogUtils.PageLoadDurationOptions, 'pageReadyMark'>>;

export interface Logger {
  trace: (params?: LoggerMessageParams) => unknown;
  error: (params?: LoggerMessageParams) => unknown;
  startScenario: (params?: LoggerMessageParams) => unknown;
  endScenario: (params?: EndScenarioParams) => unknown;
  logPageLoadAndNavigationDuration: (params?: PageLoadParams) => void;
}

export type LoggerParamsInput = LoggerParams | string | unknown[] | null;

export const getLogger = (loggerParams?: LoggerParamsInput): Logger => {
  const { defaultScenarioName, logging } = _.defaults({}, loggerParams, {
    defaultScenarioName: '',
    logging: {},
  });
  if (logging === null) {
    return defaultLogger;
  }

  const getScenarioName = (scenarioName?: string) => scenarioName || defaultScenarioName;
  const stringifyMessage = (message: unknown) => (_.isString(message) ? message : JSON.stringify(message));

  const logger = {
    trace: ({
      scenarioName,
      message,
    }: LoggerMessageParams = {}) => _.isFunction(logging.trace) && logging.trace(getScenarioName(scenarioName), stringifyMessage(message)),
    error: ({
      scenarioName,
      message,
    }: LoggerMessageParams = {}) => _.isFunction(logging.error) && logging.error(getScenarioName(scenarioName), stringifyMessage(message)),
    startScenario: ({
      scenarioName,
      message,
    }: LoggerMessageParams = {}) => _.isFunction(logging.startScenario) && logging.startScenario(getScenarioName(scenarioName), message),
    endScenario: ({
      scenarioName,
      result,
      message,
    }: EndScenarioParams = {}) => _.isFunction(logging.endScenario) && logging.endScenario(getScenarioName(scenarioName), result, message),
    logPageLoadAndNavigationDuration: ({
      pageReadyMark,
    }: PageLoadParams = {}) => logPageLoadAndNavigationDuration({ logging, pageReadyMark: pageReadyMark! }),
  };

  return logger;
};

export const useLogger = ({
  defaultScenarioName = '',
  logging,
}: LoggerParams = {}) => useMemo(() => getLogger({ defaultScenarioName, logging }), [defaultScenarioName, logging]);
